/**
 * Step 0 — mode switcher.
 *
 * Hyperlink-styled trigger rendered inline above the mode body when:
 *   - the active step is Step 0
 *   - selectedMode is non-null (a mode has been picked)
 *   - status === 'empty' (no submission has started yet)
 *
 * Opens a dropdown showing the OTHER two modes (the current mode is
 * excluded). Selecting an item calls `onSwitch`, which dispatches a
 * SET_STEP_MODE. The dropdown's open state, outside-click / Escape close,
 * focus management, and positioning are all owned by the 2.0 DropdownMenu
 * primitive — this component only supplies the trigger look + the items.
 *
 * No confirm dialog — switching modes may lose typed manual markdown
 * (the user is warned by the visible switch). Acceptable for MVP.
 */
import React from 'react';
import type { Step0Mode } from '../../state/workflowV2Types';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
} from '../../../../components/ui/DropdownMenu';
import { Icon } from '../../../../components/ui/Icon';
import styles from './ModeSwitch.module.css';

// DropdownMenu is a JSX composable; cast its parts for use from TSX (mirrors the
// DataTableAny/ButtonAny convention — the JS components' optional callbacks read
// as required under TS's JS inference).
const Menu = DropdownMenu as unknown as React.ComponentType<any>;
const MenuTrigger = DropdownMenuTrigger as unknown as React.ComponentType<any>;
const MenuContent = DropdownMenuContent as unknown as React.ComponentType<any>;
const MenuLabel = DropdownMenuLabel as unknown as React.ComponentType<any>;
const MenuItem = DropdownMenuItem as unknown as React.ComponentType<any>;

const MODE_LABEL: Record<Step0Mode, string> = {
  automatic: 'Automatic Analysis',
  manual: 'Manual Definition',
  diagram: 'Import Architecture Diagram',
};

export interface ModeSwitchProps {
  currentMode: Step0Mode;
  onSwitch: (mode: Step0Mode) => void;
}

export default function ModeSwitch({ currentMode, onSwitch }: ModeSwitchProps) {
  const otherModes = (Object.keys(MODE_LABEL) as Step0Mode[]).filter((m) => m !== currentMode);

  return (
    <Menu>
      <MenuTrigger className={styles.trigger} aria-label="Switch input method">
        <span className={styles.label}>Mode: {MODE_LABEL[currentMode]}</span>
        <Icon name="chevron-down" size={10} />
      </MenuTrigger>
      <MenuContent side="bottom" align="end">
        <MenuLabel>Switch input method</MenuLabel>
        {otherModes.map((m) => (
          <MenuItem key={m} onSelect={() => onSwitch(m)}>
            {MODE_LABEL[m]}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}
